import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listPublicBlogs } from '../api/publicBlogs';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { STATIC_BLOGS } from '../../content/staticBlogs.js';
import useIsMobile from '../../hooks/useIsMobile.js';
import MobileBottomNav from '../../mobile/components/MobileBottomNav.jsx';
import MobileHeader from '../../mobile/components/MobileHeader.jsx';

const BlogList = () => {
  const isMobile = useIsMobile();
  const [page, setPage] = useState(1);
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['public-blogs', page, query],
    queryFn: () => listPublicBlogs({ limit: 12, page, q: query }),
  });
  const remoteItems = data?.data || data || [];
  const mergedItems = (() => {
    const map = new Map();
    STATIC_BLOGS.forEach((blog) => map.set(blog.slug, blog));
    if (Array.isArray(remoteItems)) {
      remoteItems.forEach((blog) => {
        if (blog?.slug) map.set(blog.slug, { ...map.get(blog.slug), ...blog });
      });
    }
    return Array.from(map.values());
  })();
  const staticFiltered = STATIC_BLOGS.filter((blog) => {
    const term = query.trim().toLowerCase();
    if (!term) return true;
    return (
      String(blog.title || '').toLowerCase().includes(term) ||
      String(blog.excerpt || '').toLowerCase().includes(term) ||
      String(blog.body || '').toLowerCase().includes(term)
    );
  });
  const filteredItems = (Array.isArray(remoteItems) && remoteItems.length ? mergedItems : staticFiltered).filter((blog) => {
    const term = query.trim().toLowerCase();
    if (!term) return true;
    return (
      String(blog.title || '').toLowerCase().includes(term) ||
      String(blog.excerpt || '').toLowerCase().includes(term) ||
      String(blog.body || '').toLowerCase().includes(term)
    );
  });
  const items = filteredItems.slice((page - 1) * 12, page * 12);
  const effectivePagination = {
    page,
    totalPages: Math.max(1, Math.ceil(filteredItems.length / 12)),
  };

  return (
    <div className="bg-[#F7F2EA]">
      {isMobile ? <MobileHeader showBack /> : null}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#F7F2EA] to-[#F7F2EA]" />
        <div className="absolute -right-24 top-8 h-48 w-48 rounded-full bg-amber-100/70 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-40 w-40 rounded-full bg-slate-200/60 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-12">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.4em] text-amber-600">Blog</p>
              <h1 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">
                Latest from the Homevilas journal
              </h1>
              <p className="mt-3 text-base text-slate-600">
                Curated travel notes, route guides, and slow-living itineraries by our team.
              </p>
            </div>
            <div className="flex w-full max-w-md items-center gap-2 rounded-full border border-slate-200 bg-white p-1 shadow-sm">
              <input
                className="w-full border-none bg-transparent px-4 py-2 text-sm text-slate-700 outline-none"
                placeholder="Search blog articles"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setQuery(queryInput);
                    setPage(1);
                  }
                }}
              />
              <button
                className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                onClick={() => {
                  setQuery(queryInput);
                  setPage(1);
                }}
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 pb-12">
        {isLoading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            Loading blogs...
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            No blogs yet.
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((blog) => (
            <Link
              key={blog.id}
              to={`/blog/${blog.slug}`}
              className="group overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-[0_20px_40px_rgba(15,23,42,0.08)] transition hover:-translate-y-1 hover:shadow-[0_28px_60px_rgba(15,23,42,0.12)]"
            >
              <div className="h-52 bg-slate-100">
                {blog.image_url ? (
                  <img
                    src={resolveAssetUrl(blog.image_url)}
                    alt={blog.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-slate-400">No image</div>
                )}
              </div>
              <div className="p-5">
                <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
                  {blog.published_at || blog.created_at
                    ? new Date(blog.published_at || blog.created_at).toLocaleDateString()
                    : 'Blog'}
                </p>
                <h2 className="mt-2 text-lg font-semibold text-slate-900">{blog.title}</h2>
                <p className="mt-2 text-sm text-slate-600">{blog.excerpt || 'Read more on Homevilas.'}</p>
                <span className="mt-4 inline-flex items-center text-xs font-semibold uppercase tracking-[0.2em] text-amber-600">
                  Read article →
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
          <span>
            Page {effectivePagination.page} of {effectivePagination.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              className="rounded-full border border-slate-200 bg-white px-4 py-2 disabled:opacity-50"
              disabled={effectivePagination.page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            >
              Prev
            </button>
            <button
              className="rounded-full border border-slate-200 bg-white px-4 py-2 disabled:opacity-50"
              disabled={effectivePagination.page >= effectivePagination.totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {isMobile ? <MobileBottomNav /> : null}
    </div>
  );
};

export default BlogList;
